import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Bookmark, BookmarkCheck, Building2, MapPin, Calendar, 
  Trash2, ExternalLink, BarChart3, Check, X, Clock, Award, Settings, Bell, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger 
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";

const STATUS_OPTIONS = [
  { value: "saved", label: "Saved", icon: Bookmark, color: "text-[#D4AF37] bg-[#D4AF37]/10" },
  { value: "applied", label: "Applied", icon: Check, color: "text-blue-400 bg-blue-400/10" },
  { value: "interviewed", label: "Interviewed", icon: Clock, color: "text-purple-400 bg-purple-400/10" },
  { value: "offered", label: "Offered", icon: Award, color: "text-green-400 bg-green-400/10" },
  { value: "rejected", label: "Rejected", icon: X, color: "text-red-400 bg-red-400/10" },
];

export default function DashboardPage() {
  const location = useLocation();
  const { user } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  // Use user from location state if available (from AuthCallback)
  const currentUser = user || location.state?.user;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bookmarksRes, statsRes] = await Promise.all([
          fetch(`${API}/bookmarks`, { credentials: "include" }),
          fetch(`${API}/stats/dashboard`, { credentials: "include" })
        ]);

        if (bookmarksRes.ok) {
          const data = await bookmarksRes.json();
          setBookmarks(data);
        }

        if (statsRes.ok) {
          const data = await statsRes.json();
          setStats(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const updateBookmarkStatus = async (jobId, newStatus) => {
    try {
      const response = await fetch(`${API}/bookmarks/${jobId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setBookmarks(prev => 
          prev.map(b => b.job_id === jobId ? { ...b, status: newStatus } : b)
        );
        toast.success(`Status updated to ${newStatus}`);
        
        // Update stats
        const statsRes = await fetch(`${API}/stats/dashboard`, { credentials: "include" });
        if (statsRes.ok) {
          setStats(await statsRes.json());
        }
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const removeBookmark = async (jobId) => {
    try {
      const response = await fetch(`${API}/bookmarks/${jobId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        setBookmarks(prev => prev.filter(b => b.job_id !== jobId));
        toast.success("Bookmark removed");
        
        // Update stats
        const statsRes = await fetch(`${API}/stats/dashboard`, { credentials: "include" });
        if (statsRes.ok) {
          setStats(await statsRes.json());
        }
      }
    } catch (error) {
      toast.error("Failed to remove bookmark");
    }
  };

  const filteredBookmarks = activeTab === "all" 
    ? bookmarks 
    : bookmarks.filter(b => b.status === activeTab);

  const getStatusInfo = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="dashboard-page">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-medium text-white mb-2">
              Welcome back, {currentUser?.name?.split(' ')[0] || 'Researcher'}
            </h1>
            <p className="text-gray-400">
              Track your saved positions and application progress
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/settings">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-sm" data-testid="dashboard-settings-btn">
                <Bell className="w-4 h-4 mr-2" />
                Notifications
              </Button>
            </Link>
            <Link to="/sources">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-sm" data-testid="dashboard-sources-btn">
                <Globe className="w-4 h-4 mr-2" />
                Job Sources
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {STATUS_OPTIONS.map((status) => {
            const count = stats?.by_status?.[status.value] || 0;
            const StatusIcon = status.icon;
            return (
              <motion.div
                key={status.value}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="stat-card p-4 rounded-sm bg-[#0A0A0A] border border-white/10 cursor-pointer"
                onClick={() => setActiveTab(status.value)}
                data-testid={`stat-card-${status.value}`}
              >
                <div className={`inline-flex items-center justify-center w-10 h-10 rounded-sm ${status.color} mb-3`}>
                  <StatusIcon className="w-5 h-5" />
                </div>
                <p className="text-2xl font-bold text-white">{count}</p>
                <p className="text-xs text-gray-500 uppercase tracking-wide">{status.label}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#0A0A0A] border border-white/10 p-1 mb-6">
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-[#800020] data-[state=active]:text-white rounded-sm"
              data-testid="tab-all"
            >
              All ({bookmarks.length})
            </TabsTrigger>
            {STATUS_OPTIONS.map((status) => (
              <TabsTrigger
                key={status.value}
                value={status.value}
                className="data-[state=active]:bg-[#800020] data-[state=active]:text-white rounded-sm hidden md:inline-flex"
                data-testid={`tab-${status.value}`}
              >
                {status.label} ({stats?.by_status?.[status.value] || 0})
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeTab} className="mt-0">
            {filteredBookmarks.length === 0 ? (
              <div className="text-center py-16 bg-[#0A0A0A] border border-white/10 rounded-sm">
                <BookmarkCheck className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No positions yet</h3>
                <p className="text-gray-400 mb-6">
                  {activeTab === "all" 
                    ? "Start browsing and save positions you're interested in."
                    : `No positions with "${activeTab}" status.`
                  }
                </p>
                <Link to="/jobs">
                  <Button className="bg-[#D4AF37] text-black hover:bg-[#C5A028] rounded-sm" data-testid="browse-jobs-btn">
                    Browse Positions
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredBookmarks.map((bookmark, index) => {
                  const job = bookmark.job;
                  const statusInfo = getStatusInfo(bookmark.status);
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <motion.div
                      key={bookmark.bookmark_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="job-card group relative overflow-hidden rounded-sm border border-white/10 bg-[#0A0A0A] p-6"
                      data-testid={`bookmark-card-${bookmark.job_id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="inline-block px-3 py-1 bg-[#800020]/20 text-[#D4AF37] text-xs font-semibold uppercase tracking-wide rounded-sm">
                              {job?.field}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-sm ${statusInfo.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusInfo.label}
                            </span>
                          </div>
                          
                          <Link to={`/jobs/${bookmark.job_id}`}>
                            <h3 className="font-serif text-xl font-semibold text-white mb-2 group-hover:text-[#D4AF37] transition-colors truncate">
                              {job?.title}
                            </h3>
                          </Link>
                          
                          <p className="text-gray-400 mb-3 flex items-center gap-2 truncate">
                            <Building2 className="w-4 h-4 flex-shrink-0" />
                            {job?.institution} — {job?.department}
                          </p>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job?.location}, {job?.country}
                            </span>
                            {job?.deadline && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                Deadline: {new Date(job.deadline).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <Select 
                            value={bookmark.status} 
                            onValueChange={(value) => updateBookmarkStatus(bookmark.job_id, value)}
                          >
                            <SelectTrigger 
                              className="w-36 bg-white/5 border-white/10 text-white"
                              data-testid={`status-select-${bookmark.job_id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A0A] border-white/10">
                              {STATUS_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  <span className="flex items-center gap-2">
                                    <option.icon className="w-4 h-4" />
                                    {option.label}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <div className="flex gap-2">
                            {job?.source_url && (
                              <a href={job.source_url} target="_blank" rel="noopener noreferrer">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-white/20 text-white hover:bg-white/5 rounded-sm"
                                  data-testid={`apply-btn-${bookmark.job_id}`}
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              </a>
                            )}
                            
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-red-500/20 text-red-400 hover:bg-red-500/10 rounded-sm"
                                  data-testid={`remove-btn-${bookmark.job_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-white">Remove Bookmark</AlertDialogTitle>
                                  <AlertDialogDescription className="text-gray-400">
                                    Are you sure you want to remove this position from your saved list? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5">
                                    Cancel
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => removeBookmark(bookmark.job_id)}
                                    className="bg-red-600 text-white hover:bg-red-700"
                                  >
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
